
import React, { useState, useEffect } from 'react';

const SIZE = 4;

const Binary2048: React.FC = () => {
  const [board, setBoard] = useState<number[][]>([]);
  const [score, setScore] = useState(0);

  const init = () => {
    let newBoard = Array(SIZE).fill(0).map(() => Array(SIZE).fill(0));
    addRandom(newBoard);
    addRandom(newBoard);
    setBoard(newBoard);
    setScore(0);
  };

  const addRandom = (b: number[][]) => {
    const empty = [];
    for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) if (b[r][c] === 0) empty.push([r, c]);
    if (empty.length > 0) {
      const [r, c] = empty[Math.floor(Math.random() * empty.length)];
      b[r][c] = Math.random() < 0.9 ? 2 : 4;
    }
  };

  useEffect(() => init(), []);

  const move = (dir: 'u' | 'd' | 'l' | 'r') => {
    const newBoard = board.map(row => [...row]);
    let moved = false;

    const shift = (line: number[]) => {
      let filtered = line.filter(v => v !== 0);
      for (let i = 0; i < filtered.length - 1; i++) {
        if (filtered[i] === filtered[i+1]) {
          filtered[i] *= 2;
          setScore(s => s + filtered[i]);
          filtered.splice(i + 1, 1);
          moved = true;
        }
      }
      while (filtered.length < SIZE) filtered.push(0);
      return filtered;
    };

    if (dir === 'l' || dir === 'r') {
      for (let r = 0; r < SIZE; r++) {
        let line = newBoard[r];
        if (dir === 'r') line.reverse();
        let newLine = shift(line);
        if (dir === 'r') newLine.reverse();
        if (JSON.stringify(newBoard[r]) !== JSON.stringify(newLine)) moved = true;
        newBoard[r] = newLine;
      }
    } else {
      for (let c = 0; c < SIZE; c++) {
        let line = newBoard.map(row => row[c]);
        if (dir === 'd') line.reverse();
        let newLine = shift(line);
        if (dir === 'd') newLine.reverse();
        for (let r = 0; r < SIZE; r++) {
          if (newBoard[r][c] !== newLine[r]) moved = true;
          newBoard[r][c] = newLine[r];
        }
      }
    }

    if (moved) {
      addRandom(newBoard);
      setBoard(newBoard);
    }
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault();
      if (e.key === 'ArrowLeft') move('l');
      if (e.key === 'ArrowRight') move('r');
      if (e.key === 'ArrowUp') move('u');
      if (e.key === 'ArrowDown') move('d');
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [board]);

  const getColor = (val: number) => {
    const map: Record<number, string> = {
      2: 'bg-slate-700 text-slate-300',
      4: 'bg-slate-600 text-slate-100',
      8: 'bg-blue-900 text-blue-100',
      16: 'bg-blue-700 text-white',
      32: 'bg-indigo-600 text-white',
      64: 'bg-indigo-500 text-white shadow-[0_0_15px_rgba(99,102,241,0.3)]',
      128: 'bg-purple-600 text-white shadow-[0_0_20px_rgba(168,85,247,0.4)]',
      256: 'bg-purple-500 text-white',
      512: 'bg-pink-600 text-white',
      1024: 'bg-pink-500 text-white scale-105',
      2048: 'bg-amber-500 text-white scale-110 shadow-[0_0_30px_rgba(245,158,11,0.6)] animate-pulse'
    };
    return map[val] || 'bg-slate-800';
  };

  return (
    <div className="flex flex-col items-center game-container">
      <div className="flex justify-between w-full max-w-[360px] mb-8 glass p-6 rounded-3xl items-center">
        <div className="flex flex-col">
          <span className="text-[10px] font-black uppercase text-amber-500 tracking-widest">Aggregate Score</span>
          <span className="text-3xl font-black">{score.toLocaleString()}</span>
        </div>
        <button onClick={init} className="bg-slate-800 hover:bg-slate-700 px-5 py-2 rounded-xl text-xs font-bold transition-all border border-white/5">CLEAR DATA</button>
      </div>

      <div className="bg-slate-900/80 p-3 rounded-[2rem] border-4 border-slate-800 shadow-2xl relative overflow-hidden">
        <div className="grid grid-cols-4 gap-3 w-[min(90vw,340px)] h-[min(90vw,340px)]">
          {board.flat().map((val, i) => (
            <div 
              key={i} 
              className={`flex items-center justify-center text-2xl font-black rounded-2xl transition-all duration-200 transform-gpu ${getColor(val)}`}
            >
              {val || ''}
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-12 grid grid-cols-3 gap-3">
        <div />
        <button onClick={() => move('u')} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">↑</button>
        <div />
        <button onClick={() => move('l')} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">←</button>
        <button onClick={() => move('d')} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">↓</button>
        <button onClick={() => move('r')} className="w-16 h-16 glass rounded-2xl flex items-center justify-center text-2xl">→</button>
      </div>
    </div>
  );
};

export default Binary2048;
